/**
 * ID29 export column catalog — single FE source mirroring BE `ExportColumn`.
 * Order = excel column order. `level` groups columns in the picker UI
 * (주문/상품 레벨). `labelKey` resolves through the `orders.export.column.*`
 * i18n namespace. Keys + order must stay 1:1 with BE; BE ignores unknown keys
 * and falls back to "all" when the requested set is empty.
 */
export type ExportColumnLevel = 'order' | 'item' | 'collection';

export interface ExportColumnDef {
  key: string;
  labelKey: string;
  level: ExportColumnLevel;
}

export const EXPORT_COLUMNS: ReadonlyArray<ExportColumnDef> = [
  { key: 'orderNo', labelKey: 'orders.export.column.orderNo', level: 'order' },
  { key: 'orderedAt', labelKey: 'orders.export.column.orderedAt', level: 'order' },
  { key: 'loginId', labelKey: 'orders.export.column.loginId', level: 'order' },
  { key: 'email', labelKey: 'orders.export.column.email', level: 'order' },
  { key: 'nationality', labelKey: 'orders.export.column.nationality', level: 'order' },
  { key: 'ordererName', labelKey: 'orders.export.column.ordererName', level: 'order' },
  { key: 'ordererPhone', labelKey: 'orders.export.column.ordererPhone', level: 'order' },
  { key: 'productCode', labelKey: 'orders.export.column.productCode', level: 'item' },
  { key: 'productName', labelKey: 'orders.export.column.productName', level: 'item' },
  // QA-467 정산용(order #74) — 자체상품코드(주문 시점), 아티스트·소속사(현재 상품·아티스트 설정). 상품명 다음 3칸.
  { key: 'selfProductCode', labelKey: 'orders.export.column.selfProductCode', level: 'item' },
  // QA-575 — 자체옵션코드 단독 칸(order #116). [자체상품코드]는 BE 가 자체옵션코드를 이어 붙여 준다.
  { key: 'selfOptionCode', labelKey: 'orders.export.column.selfOptionCode', level: 'item' },
  { key: 'artist', labelKey: 'orders.export.column.artist', level: 'item' },
  { key: 'agency', labelKey: 'orders.export.column.agency', level: 'item' },
  { key: 'optionLabel', labelKey: 'orders.export.column.optionLabel', level: 'item' },
  { key: 'quantity', labelKey: 'orders.export.column.quantity', level: 'item' },
  { key: 'unitPrice', labelKey: 'orders.export.column.unitPrice', level: 'item' },
  { key: 'itemTotal', labelKey: 'orders.export.column.itemTotal', level: 'item' },
  { key: 'paymentAmount', labelKey: 'orders.export.column.paymentAmount', level: 'order' },
  // QA-358 — 해외몰 결제 통화·현지 승인 금액·적용 환율(BE order #52, paymentAmount 직후 3칸 확정).
  //   원화 결제는 KRW·실결제액·환율 빈칸, 이관 해외몰 주문은 이관 보정 전까지 USD·빈칸.
  { key: 'chargeCurrency', labelKey: 'orders.export.column.chargeCurrency', level: 'order' },
  { key: 'chargeAmount', labelKey: 'orders.export.column.chargeAmount', level: 'order' },
  { key: 'chargeKrwPerUnit', labelKey: 'orders.export.column.chargeKrwPerUnit', level: 'order' },
  { key: 'paymentMethod', labelKey: 'orders.export.column.paymentMethod', level: 'order' },
  { key: 'paymentStatus', labelKey: 'orders.export.column.paymentStatus', level: 'order' },
  { key: 'fulfillmentStatus', labelKey: 'orders.export.column.fulfillmentStatus', level: 'order' },
  // QA-618 — 택배사(courier_companies 이름)·송장번호. BE ExportColumn courierName·trackingNo, 배송/픽업 상태 다음.
  { key: 'courierName', labelKey: 'orders.export.column.courierName', level: 'order' },
  { key: 'trackingNo', labelKey: 'orders.export.column.trackingNo', level: 'order' },
  { key: 'claimStatus', labelKey: 'orders.export.column.claimStatus', level: 'order' },
  { key: 'channel', labelKey: 'orders.export.column.channel', level: 'order' },
  // 국가몰(country-mall) — be-order-mall ExportColumn key="mall"(주문레벨). ★BE enum 선언순서
  //   = 엑셀 컬럼순서라 **channel 직후·recipientName 앞**이 1:1 정본(orderedAt 직후 아님).
  //   신규 컬럼이라 useExportColumnSelection known-snapshot 이 기본 포함한다.
  { key: 'mall', labelKey: 'orders.export.column.mall', level: 'order' },
  { key: 'recipientName', labelKey: 'orders.export.column.recipientName', level: 'order' },
  { key: 'recipientPhone', labelKey: 'orders.export.column.recipientPhone', level: 'order' },
  { key: 'recipientPostalCode', labelKey: 'orders.export.column.recipientPostalCode', level: 'order' },
  { key: 'recipientAddress', labelKey: 'orders.export.column.recipientAddress', level: 'order' },
  { key: 'recipientCountryCode', labelKey: 'orders.export.column.recipientCountryCode', level: 'order' },
  // [233] 해외(글로벌) 배송지 확장 컬럼 — 주문 레벨. 키·순서는 BE ExportColumn
  //   (RECIPIENT_ENGLISH_NAME~RECIPIENT_REMARKS, recipientCountryCode 다음·additionalInput 이전)
  //   과 1:1 동일 정본. remarks 키는 BE RECIPIENT_REMARKS("remarks") 와 동일하게 'remarks'.
  { key: 'recipientEnglishName', labelKey: 'orders.export.column.recipientEnglishName', level: 'order' },
  { key: 'recipientCity', labelKey: 'orders.export.column.recipientCity', level: 'order' },
  { key: 'recipientStateProvince', labelKey: 'orders.export.column.recipientStateProvince', level: 'order' },
  // QA-555(order #109) 송장 배송주소 — 우편번호 주소1 주소2 도시 주/도를 한 칸으로(해외 송장용).
  { key: 'recipientFullAddress', labelKey: 'orders.export.column.recipientFullAddress', level: 'order' },
  { key: 'recipientMobile', labelKey: 'orders.export.column.recipientMobile', level: 'order' },
  { key: 'remarks', labelKey: 'orders.export.column.remarks', level: 'order' },
  // QA-454(order #69) — 배송 메모(주문서 배송 요청사항, 운송장 인쇄). BE ExportColumn key="deliveryMemo", 비고 다음 칸.
  { key: 'deliveryMemo', labelKey: 'orders.export.column.deliveryMemo', level: 'order' },
  // QA-469(order #72) — 착불 배송비(결제 금액 미포함, 없으면 0). BE ExportColumn key="codShippingFee", 배송 메모 다음 칸.
  { key: 'codShippingFee', labelKey: 'orders.export.column.codShippingFee', level: 'order' },
  // QA-474(order #79) — 선결제 배송비·주문 상품 종류 수·주문 총 수량. 주문의 첫 상품 행에만 값(합계 중복 방지). 착불 배송비 다음.
  { key: 'shippingFee', labelKey: 'orders.export.column.shippingFee', level: 'order' },
  { key: 'orderItemKinds', labelKey: 'orders.export.column.orderItemKinds', level: 'order' },
  { key: 'orderTotalQuantity', labelKey: 'orders.export.column.orderTotalQuantity', level: 'order' },
  // QA-486(order #82) — (도매) 양식 칸. 주문 총 수량 다음, BE ExportColumn 순서 그대로. 주문 단위 합계 칸은 첫 상품 행에만 값.
  { key: 'orderStatusLabel', labelKey: 'orders.export.column.orderStatusLabel', level: 'order' },
  { key: 'memberName', labelKey: 'orders.export.column.memberName', level: 'order' },
  { key: 'paidAt', labelKey: 'orders.export.column.paidAt', level: 'order' },
  { key: 'depositorName', labelKey: 'orders.export.column.depositorName', level: 'order' },
  { key: 'receiptRequested', labelKey: 'orders.export.column.receiptRequested', level: 'order' },
  { key: 'gradeName', labelKey: 'orders.export.column.gradeName', level: 'order' },
  { key: 'goodsUnitPrice', labelKey: 'orders.export.column.goodsUnitPrice', level: 'item' },
  { key: 'optionAmount', labelKey: 'orders.export.column.optionAmount', level: 'item' },
  { key: 'gradeDiscountTotal', labelKey: 'orders.export.column.gradeDiscountTotal', level: 'order' },
  { key: 'productDiscountTotal', labelKey: 'orders.export.column.productDiscountTotal', level: 'order' },
  { key: 'gradeDiscountInfo', labelKey: 'orders.export.column.gradeDiscountInfo', level: 'order' },
  { key: 'itemGradeDiscount', labelKey: 'orders.export.column.itemGradeDiscount', level: 'item' },
  { key: 'itemPayableAmount', labelKey: 'orders.export.column.itemPayableAmount', level: 'item' },
  { key: 'adminMemo', labelKey: 'orders.export.column.adminMemo', level: 'order' },
  // QA-190(be-o BE큐): 결제상품 '추가 입력·동의' 데이터. BE ExportColumn key="additionalInput"
  // (주문레벨, 평문+암호화 PII 마스킹은 BE 처리). 회원ID/이메일/국적은 기존 컬럼으로 이미 제공.
  { key: 'additionalInput', labelKey: 'orders.export.column.additionalInput', level: 'order' },
  // QA-576 — 중국몰 주문서 추가정보(신분증 번호·위챗), 주문 단위(order #118, 추가 입력/동의 다음).
  { key: 'cnIdNumber', labelKey: 'orders.export.column.cnIdNumber', level: 'order' },
  { key: 'cnWechatId', labelKey: 'orders.export.column.cnWechatId', level: 'order' },
  // QA-468(order #75) — 상품 추가입력 전체(기간 내 모든 라벨을 "[추가입력] 라벨" 칸으로). BE PRODUCT_COLLECTION_INPUTS(맨 끝).
  //   라벨별 칸은 동적 키 productCollectionInput:<라벨>(라벨 목록 API) — 아래 helper.
  { key: 'productCollectionInputs', labelKey: 'orders.export.column.productCollectionInputs', level: 'collection' },
];

export const EXPORT_COLUMN_KEYS: ReadonlyArray<string> = EXPORT_COLUMNS.map((c) => c.key);

export const ORDER_LEVEL_COLUMNS = EXPORT_COLUMNS.filter((c) => c.level === 'order');
export const ITEM_LEVEL_COLUMNS = EXPORT_COLUMNS.filter((c) => c.level === 'item');
export const COLLECTION_LEVEL_COLUMNS = EXPORT_COLUMNS.filter((c) => c.level === 'collection');

/** QA-468 — 상품 추가입력 라벨 하나만 고르는 칸 키(예: "productCollectionInput:국적 nationality"). */
export const COLLECTION_INPUT_PREFIX = 'productCollectionInput:';
export const isCollectionInputKey = (key: string): boolean => key.startsWith(COLLECTION_INPUT_PREFIX);

/** 칸 이름 — 고정 칸은 i18n, 추가입력 라벨 칸은 "[추가입력] 라벨"(BE 머리글과 같음). 모르는 키는 키 그대로. */
export const exportColumnLabel = (t: (key: string, fallback: string) => string, key: string): string => {
  if (isCollectionInputKey(key)) return `[추가입력] ${key.slice(COLLECTION_INPUT_PREFIX.length)}`;
  const c = EXPORT_COLUMNS.find((x) => x.key === key);
  return c ? t(c.labelKey, key) : key;
};
